// src/components/WaveformSingle.tsx

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { AudioWithUrl } from '../types/audio.types';

interface WaveformSingleProps {
  audio: AudioWithUrl;
  startTime?: number;
  endTime?: number;
}

const WaveformSingle: React.FC<WaveformSingleProps> = ({
  audio,
  startTime = 2,
  endTime = 4,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [rawWaveformData, setRawWaveformData] = useState<Float32Array | null>(null);
  const [normalizedData, setNormalizedData] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1); // 1x, 2x, 3x, 4x
  const [visibleTimeRange, setVisibleTimeRange] = useState({ start: startTime, end: endTime });
  const normalizationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Mesmas dimensões do WaveformComparison
  const baseCanvasWidth = 2400;
  const canvasWidth = baseCanvasWidth * zoomLevel;
  const canvasHeight = 180;

  // Processar e carregar dados brutos do áudio
  const loadRawAudioData = useCallback(async (): Promise<Float32Array> => {
    if (!audio.signedUrl) {
      throw new Error('URL do áudio não disponível');
    }

    try {
      const response = await fetch(audio.signedUrl);
      const arrayBuffer = await response.arrayBuffer();

      // Se já existe um contexto de áudio, tenta fechá-lo apenas se não estiver fechado
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        await audioContextRef.current.close().catch(console.error);
      }
      
      // Cria um novo contexto de áudio
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      return audioBuffer.getChannelData(0);
    } catch (error) {
      console.error('Erro ao carregar áudio:', error);
      throw error;
    }
  }, [audio.signedUrl]);

  // Normalizar dados para o trecho visível
  const normalizeVisibleSection = useCallback((
    rawData: Float32Array,
    visibleStart: number,
    visibleEnd: number,
    sampleRate: number = 44100
  ): number[] => {
    const startSample = Math.floor(visibleStart * sampleRate);
    const endSample = Math.floor(visibleEnd * sampleRate);
    const visibleSamples = rawData.slice(startSample, endSample);
    
    const targetPoints = 2000 * zoomLevel;
    const step = Math.max(1, Math.floor(visibleSamples.length / targetPoints));
    const downsampled: number[] = [];

    // Downsample usando RMS
    for (let i = 0; i < visibleSamples.length; i += step) {
      const chunk = visibleSamples.slice(i, i + step);
      const rms = Math.sqrt(chunk.reduce((sum, val) => sum + val * val, 0) / chunk.length);
      downsampled.push(rms);
    }

    // Normalizar apenas o trecho visível
    const maxAmplitude = Math.max(...downsampled.map(Math.abs));
    return maxAmplitude > 0 
      ? downsampled.map(val => val / maxAmplitude)
      : downsampled;
  }, [zoomLevel]);

  // Handler para mudanças na visualização
  const handleVisibleRangeChange = useCallback(() => {
    if (!scrollContainerRef.current || !rawWaveformData) return;

    const container = scrollContainerRef.current;
    const { scrollLeft, clientWidth, scrollWidth } = container;
    
    // Calcular o range visível baseado no scroll
    const duration = endTime - startTime;
    const visibleStart = startTime + (scrollLeft / scrollWidth) * duration;
    const visibleEnd = visibleStart + (clientWidth / scrollWidth) * duration;

    // Cancelar normalização pendente
    if (normalizationTimeoutRef.current) {
      clearTimeout(normalizationTimeoutRef.current);
    }

    // Agendar nova normalização após 2 segundos apenas se houve mudança real na visualização
    const currentRange = { start: visibleStart, end: visibleEnd };
    if (
      Math.abs(currentRange.start - visibleTimeRange.start) > 0.001 ||
      Math.abs(currentRange.end - visibleTimeRange.end) > 0.001
    ) {
      setVisibleTimeRange(currentRange);
      normalizationTimeoutRef.current = setTimeout(() => {
        const normalized = normalizeVisibleSection(
          rawWaveformData,
          visibleStart,
          visibleEnd
        );
        setNormalizedData(normalized);
      }, 2000);
    }
  }, [rawWaveformData, startTime, endTime, normalizeVisibleSection, visibleTimeRange]);

  // Carregar dados brutos do áudio
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const rawData = await loadRawAudioData();
        setRawWaveformData(rawData);
        
        // Normalização inicial do trecho visível
        const normalized = normalizeVisibleSection(
          rawData,
          startTime,
          endTime
        );
        setNormalizedData(normalized);
        
        // Definir range visível inicial
        setVisibleTimeRange({ start: startTime, end: endTime });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar áudio');
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Cleanup
    return () => {
      if (normalizationTimeoutRef.current) {
        clearTimeout(normalizationTimeoutRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(console.error);
      }
    };
  }, [loadRawAudioData, normalizeVisibleSection, startTime, endTime]);

  const drawWaveform = useCallback((canvas: HTMLCanvasElement, data: number[], color: string) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const middle = height / 2;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw background
    ctx.fillStyle = '#F9FAFB';
    ctx.fillRect(0, 0, width, height);

    // Draw center line
    ctx.strokeStyle = '#D1D5DB';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, middle);
    ctx.lineTo(width, middle);
    ctx.stroke();

    // Draw amplitude grid lines
    ctx.strokeStyle = '#E5E7EB';
    ctx.lineWidth = 0.5;
    ctx.setLineDash([2, 2]);

    [-0.75, -0.5, -0.25, 0.25, 0.5, 0.75].forEach(amp => {
      const y = middle - (amp * middle * 0.9);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    });

    ctx.setLineDash([]);

    // Draw time markers
    ctx.fillStyle = '#9CA3AF';
    ctx.font = '10px monospace';
    const duration = endTime - startTime;
    const pixelsPerSecond = width / duration;
    
    for (let t = 0; t <= duration; t += 0.5) {
      const x = t * pixelsPerSecond;
      ctx.setLineDash([2, 2]);
      ctx.strokeStyle = '#E5E7EB';
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      ctx.setLineDash([]);
      
      const timeLabel = (startTime + t).toFixed(1) + 's';
      ctx.fillText(timeLabel, x + 4, 12);
    }

    if (data.length === 0) return;

    // Draw symmetric waveform (top half)
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    const step = width / data.length;

    // Draw top half
    ctx.beginPath();
    for (let i = 0; i < data.length; i++) {
      const x = i * step;
      const amplitude = Math.abs(data[i]);
      const y = middle - (amplitude * middle * 0.85);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Draw bottom half
    ctx.beginPath();
    for (let i = 0; i < data.length; i++) {
      const x = i * step;
      const amplitude = Math.abs(data[i]);
      const y = middle + (amplitude * middle * 0.85);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Fill area for better visualization
    ctx.globalAlpha = 0.15;
    ctx.fillStyle = color;

    // Fill top half
    ctx.beginPath();
    for (let i = 0; i < data.length; i++) {
      const x = i * step;
      const amplitude = Math.abs(data[i]);
      const y = middle - (amplitude * middle * 0.85);
      if (i === 0) {
        ctx.moveTo(x, middle);
        ctx.lineTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.lineTo(width, middle);
    ctx.closePath();
    ctx.fill();

    // Fill bottom half
    ctx.beginPath();
    for (let i = 0; i < data.length; i++) {
      const x = i * step;
      const amplitude = Math.abs(data[i]);
      const y = middle + (amplitude * middle * 0.85);
      if (i === 0) {
        ctx.moveTo(x, middle);
        ctx.lineTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.lineTo(width, middle);
    ctx.closePath();
    ctx.fill();

    ctx.globalAlpha = 1.0;
  }, [startTime, endTime]);

  // Render waveform when normalized data changes
  useEffect(() => {
    if (!canvasRef.current || !normalizedData.length) return;
    drawWaveform(canvasRef.current, normalizedData, '#4F46E5');
  }, [normalizedData, drawWaveform]);

  // Controles de zoom
  const handleZoomIn = () => {
    if (zoomLevel < 4) {
      setZoomLevel(prev => prev + 1);
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel > 1) {
      setZoomLevel(prev => prev - 1);
    }
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  // Navegação por scroll
  const scrollToPosition = (position: 'start' | 'middle' | 'end') => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollWidth = container.scrollWidth - container.clientWidth;
    let scrollTo = 0;

    switch (position) {
      case 'start':
        scrollTo = 0;
        break;
      case 'middle':
        scrollTo = scrollWidth / 2;
        break;
      case 'end':
        scrollTo = scrollWidth;
        break;
    }

    container.scrollTo({ left: scrollTo, behavior: 'smooth' });
  };

  // Adicionar/remover listeners de scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Adicionar listener de scroll
    container.addEventListener('scroll', handleVisibleRangeChange);
    
    // Primeira chamada para processar a visualização inicial
    handleVisibleRangeChange();

    return () => {
      container.removeEventListener('scroll', handleVisibleRangeChange);
    };
  }, [handleVisibleRangeChange]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
          </svg>
          Waveform do Áudio
        </h3>
        <div className="h-40 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-3 border-indigo-600 border-t-transparent mb-2"></div>
            <p className="text-sm text-gray-600">Processando waveform...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
          </svg>
          Waveform do Áudio
        </h3>
        <div className="h-40 flex items-center justify-center bg-red-50 rounded-lg border border-red-200">
          <div className="text-center px-4">
            <svg
              className="mx-auto h-8 w-8 text-red-600 mb-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-red-700 font-medium mb-1">Erro ao carregar waveform</p>
            <p className="text-xs text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
            Waveform do Áudio
          </h3>
          <div className="flex items-center gap-1">
            {/* Navegação rápida */}
            <button
              onClick={() => scrollToPosition('start')}
              className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition"
              title="Ir para o início"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => scrollToPosition('middle')}
              className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition"
              title="Ir para o meio"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </button>
            <button
              onClick={() => scrollToPosition('end')}
              className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition"
              title="Ir para o fim"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Visualização simétrica normalizada • Intervalo: <span className="font-medium">{startTime}s - {endTime}s</span>
        </p>

        {/* Controles de Zoom */}
        <div className="flex items-center gap-3 py-3 border-t border-b border-gray-200">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Zoom:</span>
          </div>
          
          <button
            onClick={handleZoomOut}
            disabled={zoomLevel <= 1}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            title="Diminuir zoom"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>

          <div className="flex items-center gap-1">
            {[1, 2, 3, 4].map(level => (
              <button
                key={level}
                onClick={() => setZoomLevel(level)}
                className={`w-8 h-8 rounded ${
                  zoomLevel === level
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                } text-xs font-semibold transition`}
                title={`Zoom ${level}x`}
              >
                {level}x
              </button>
            ))}
          </div>

          <button
            onClick={handleZoomIn}
            disabled={zoomLevel >= 4}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            title="Aumentar zoom"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          <button
            onClick={handleResetZoom}
            className="ml-2 px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            title="Resetar zoom"
          >
            Resetar
          </button>

          <div className="ml-auto flex items-center gap-2 text-xs text-gray-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Role horizontalmente para navegar</span>
          </div>
        </div>
      </div>

      {/* Waveform Canvas com Scroll Horizontal */}
      <div
        ref={scrollContainerRef}
        className="relative overflow-x-auto rounded-lg border border-gray-300 shadow-inner bg-gray-50"
        style={{ maxHeight: '200px' }}
      >
        <canvas
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          className="border-0"
        />
        
        {/* Indicador de Scroll */}
        <div className="absolute bottom-2 right-2 bg-white bg-opacity-90 px-2 py-1 rounded text-xs text-gray-600 shadow-sm flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          Zoom {zoomLevel}x • Role para navegar
        </div>
      </div>

      {/* Notas metodológicas */}
      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <svg
            className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <p className="text-xs text-amber-800 font-semibold mb-1">
              Notas Metodológicas
            </p>
            <ul className="text-xs text-amber-700 space-y-0.5">
              <li>• <strong>Normalização:</strong> Amplitudes divididas pelo valor máximo absoluto</li>
              <li>• <strong>RMS:</strong> Root Mean Square usado para downsampling</li>
              <li>• <strong>Zoom:</strong> Níveis 1x a 4x • Marcadores temporais a cada 0.5s</li>
              <li>• <strong>Navegação:</strong> Use os botões ⏮ ⏺ ⏭ ou scroll horizontal</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaveformSingle;
